/**
 * Draws the scrim the theme's drawers have no ::backdrop for from 990px up.
 *
 * Below that width theme-drawer opens a panel with showModal(), and the native
 * ::backdrop is both the scrim and the click target that dismisses it. Above it
 * the panel is a non-modal dialog, which has no ::backdrop at all — so without
 * this the panel floats over a page that keeps its own ground, and clicking
 * away does nothing. Escape and the close button work either way.
 *
 * `chat-drawer` is excluded, matching mab-base.css: it is the one drawer meant
 * to sit alongside the page rather than over it, so it keeps 4.1.4's squeeze.
 *
 * The scrim is one element, faded by mab-base.css and taken out of the DOM once
 * the fade ends so it never intercepts a click while invisible.
 */

const SIDEBAR_QUERY = window.matchMedia('(min-width: 990px)');
const OVERLAY_DRAWERS = 'theme-drawer[open]:not([id="chat-drawer"])';

class DrawerScrim {
  /** @type {HTMLDivElement | null} */
  #scrim = null;

  constructor() {
    document.addEventListener('theme-drawer:open', this.#sync);
    document.addEventListener('theme-drawer:close', this.#sync);
    SIDEBAR_QUERY.addEventListener('change', this.#sync);

    // The restore path sets [open] before this module loads and dispatches
    // `theme-drawer:restoring` rather than `theme-drawer:open`, so a drawer
    // carried over from the previous page needs its scrim from here.
    this.#sync();
  }

  /** @returns {(HTMLElement & { close(): Promise<void> }) | null} */
  #openDrawer() {
    return /** @type {any} */ (document.querySelector(OVERLAY_DRAWERS));
  }

  #sync = () => {
    const drawer = SIDEBAR_QUERY.matches ? this.#openDrawer() : null;

    if (!drawer) {
      this.#dismiss();
      return;
    }
    if (this.#scrim) return;

    this.#scrim = document.createElement('div');
    this.#scrim.className = 'mab-drawer-scrim';
    // Read the open drawer at click time: a second drawer may have opened over
    // the first, and the scrim should always dismiss the one on top.
    this.#scrim.addEventListener('click', () => this.#openDrawer()?.close());
    document.body.append(this.#scrim);

    // Two frames: the element has to be painted at opacity 0 before the class
    // that raises it lands, or the transition has no start value to run from.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#scrim?.classList.add('mab-drawer-scrim--visible'));
    });
  };

  /**
   * Fades the scrim out and drops it once the fade ends, so it never snaps off
   * while the panel is still sliding — the contract theme-drawer-styles states
   * for the native ::backdrop. `transitioncancel` covers a reopen mid-fade, and
   * the duration guard covers reduced motion, where no transition runs at all.
   */
  #dismiss() {
    const scrim = this.#scrim;
    if (!scrim) return;
    this.#scrim = null;

    scrim.classList.remove('mab-drawer-scrim--visible');
    if (getComputedStyle(scrim).transitionDuration === '0s') {
      scrim.remove();
      return;
    }
    scrim.addEventListener('transitionend', () => scrim.remove(), { once: true });
    scrim.addEventListener('transitioncancel', () => scrim.remove(), { once: true });
  }
}

new DrawerScrim();
